/**
 * Formato único de las líneas de consola de la sincronización.
 *
 * Cada rama del envío ya escribía su resultado DOS veces: una como fila del
 * cuadro de resultados --`{registro, no, accion, ok, mensaje}`, homologada en
 * los siete maestros-- y otra a la consola, con texto escrito a mano. De ahí
 * salía toda la divergencia: la entidad desaparecía del mensaje en tres
 * servicios, el mismo registro se nombraba de tres formas según la rama, y
 * "BC" y "Business Central" convivían en la misma familia de mensajes.
 *
 * Acá la consola se DERIVA de esa misma fila. Un maestro nuevo queda congruente
 * sin que nadie tenga que acordarse del formato.
 *
 * Tres decisiones detrás de lo que se ve:
 *
 * 1. **El prefijo `[sync:entidad]` va siempre.** En la fase 2 de `syncAll`
 *    corren cuatro servicios en paralelo y sus líneas se entrelazan; filtrando
 *    por `[sync:` se ve el flujo completo y por `[sync:cajas]` una sola
 *    entidad. Sin el prefijo, `Error al sincronizar 42:` no dice qué es 42.
 *
 * 2. **Un solo identificador en todas las ramas**: `No X (id Y) «etiqueta»`.
 *    Antes la baja usaba el `No`, el guardado la descripción y el error sólo el
 *    `id`.
 *
 * 3. **El nivel lo decide el resultado, no la rama**, con el mismo criterio del
 *    contrato con Business Central --si se procesó, no si gustó el resultado--:
 *
 *      log    lo que se aplicó: baja aplicada, guardado, baja local
 *      warn   el rechazo de negocio: BC contestó y dijo que no
 *      error  lo que impide seguir: la llamada entera, una excepción
 *
 *    Una baja rechazada era `console.error` y no es un error: es un resultado
 *    correcto. Y un fallo de red dejaba 21 líneas rojas para 20 registros
 *    --una del lote y una por registro--; ahora deja una roja y N avisos.
 */

const prefijo = (entidad) => `[sync:${entidad}]`;

// Las tablas locales no se llaman igual que la entidad: la de tiendas es
// "tpvs" y la de usuarios "users". Se traducen para que el borrado y la
// sincronización posterior de un mismo registro compartan prefijo -- filtrando
// por [sync:tiendas] se ve su vida entera, no la mitad.
const ENTIDAD_POR_TABLA = {
  users: "usuarios",
  tpvs: "tiendas",
  clients: "clientes",
  clientAddresses: "direcciones",
  aperturaDeCaja: "aperturas de caja",
  cierreDeCaja: "cierres de caja",
};

export const entidadDeTabla = (tabla) => ENTIDAD_POR_TABLA[tabla] || tabla;

// «No 3 (id 12) «Matutino»» -- las partes que falten simplemente no aparecen.
const identificar = (fila, id) => {
  const partes = [];
  if (fila?.no) partes.push(`No ${fila.no}`);
  if (id !== undefined && id !== null) partes.push(`(id ${id})`);
  if (fila?.registro) partes.push(`«${fila.registro}»`);
  return partes.join(" ");
};

const BAJA_APLICADA = { texto: "baja aplicada", nivel: "log" };
const BAJA_RECHAZADA = { texto: "baja rechazada", nivel: "warn" };

// La rama la pasa el llamador, que es el único que sabe si el registro venía
// marcado para borrar. Por la fila sola no alcanza: cuando Business Central
// RECHAZA una baja devuelve `Accion: "error"`, así que la fila de una baja
// rechazada es indistinguible de la de un guardado fallido -- que es justo la
// distinción que hay que preservar.
const RAMAS = {
  baja: (fila) => (fila?.ok ? BAJA_APLICADA : BAJA_RECHAZADA),
  "baja-local": () => ({ texto: "baja local", nivel: "log" }),
  "no-enviada": () => ({ texto: "no enviada", nivel: "warn" }),
};

// Sin rama se deduce de la fila. Sirve para los flujos que no tienen borrado,
// como los cierres de caja.
const ramaDeLaFila = (fila) => {
  if (fila?.accion === "deleted") return fila.ok ? BAJA_APLICADA : BAJA_RECHAZADA;
  return fila?.ok
    ? { texto: "guardado", nivel: "log" }
    : { texto: "guardado fallido", nivel: "warn" };
};

const escribir = (nivel, texto) => {
  if (nivel === "error") console.error(texto);
  else if (nivel === "warn") console.warn(texto);
  else console.log(texto);
};

/**
 * Anota una fila del cuadro de resultados y la devuelve, para poder usarla
 * directamente en el `detalle.push(...)` que ya existía.
 *
 * @param {string} entidad - "turnos", "cajas", "clientes"...
 * @param {{registro: string, no: string|null, accion: string, ok: boolean, mensaje: string}} fila
 * @param {number|string} [id] - Id local del registro.
 * @param {"baja"|"baja-local"|"no-enviada"} [rama] - Lo que el llamador sabe y
 *   la fila no: si el registro venía marcado para borrar.
 * @returns {object} La misma fila, sin tocar.
 */
export const anotarFila = (entidad, fila, id, rama) => {
  const { texto, nivel } = RAMAS[rama] ? RAMAS[rama](fila) : ramaDeLaFila(fila);
  const motivo = (fila?.mensaje || "").trim();

  escribir(
    nivel,
    [`${prefijo(entidad)} ${texto}`, identificar(fila, id), motivo && `— ${motivo}`]
      .filter(Boolean)
      .join(" ")
  );

  return fila;
};

/** Antes de mandar el lote: cuántos registros viajan. */
export const anotarInicioDeLote = (entidad, cantidad) => {
  console.log(
    `${prefijo(entidad)} lote · ${cantidad} ${
      cantidad === 1 ? "pendiente" : "pendientes"
    } en un solo envío`
  );
};

/**
 * Cierra el lote con el mismo recuento que ve el usuario en el cuadro.
 * No existía en consola: había que sumar las líneas a ojo.
 */
export const anotarResumenDelLote = (entidad, lote) => {
  if (!lote) return;
  const { total = 0, correctos = 0, errores = 0 } = lote;
  escribir(
    errores > 0 ? "warn" : "log",
    `${prefijo(entidad)} ${total} · ${correctos} correctos · ${errores} ${
      errores === 1 ? "error" : "errores"
    }`
  );
};

/**
 * La llamada entera no llegó a procesarse. Es la ÚNICA línea roja de un fallo
 * de red: los registros que quedan sin aplicar se anotan después como avisos.
 */
export const anotarFalloDeLote = (entidad, motivo, cantidad) => {
  console.error(
    `${prefijo(entidad)} lote fallido · ${cantidad} ${
      cantidad === 1 ? "registro sin aplicar" : "registros sin aplicar"
    } — ${motivo}`
  );
};

/** Una excepción que corta el flujo. El contexto dice dónde. */
export const anotarError = (entidad, contexto, error) => {
  console.error(`${prefijo(entidad)} ${contexto}`, error);
};

/** Algo que no impide seguir pero conviene que se vea. */
export const anotarAviso = (entidad, texto) => {
  console.warn(`${prefijo(entidad)} ${texto}`);
};

/** Algo que pasó y vale la pena poder seguir en la consola. */
export const anotarPaso = (entidad, texto) => {
  console.log(`${prefijo(entidad)} ${texto}`);
};

// ─── La lectura ────────────────────────────────────────────────────────────
//
// El envío ya estaba homologado; la lectura no. Cada servicio contaba lo suyo a
// su manera --"Clientes: N traídos", "Sincronización de cupones completada: N
// cupones sincronizados", "Obtenidos N precios (M del servidor)"-- y encima el
// avance, el resultado y el avance del checkpoint salían de tres sitios
// distintos para una sola operación.

const separarMiles = (numero) =>
  typeof numero === "number" && Number.isFinite(numero) ? numero.toLocaleString("es-MX") : numero;

/** Avance de un bloque, mientras la lectura sigue en curso. */
export const anotarAvanceDeLectura = (entidad, procesados) => {
  console.log(`${prefijo(entidad)} leyendo · ${separarMiles(procesados)}`);
};

/**
 * Cierre de la lectura, en una sola línea.
 *
 * La marca es el checkpoint que queda guardado: es lo primero que hay que mirar
 * cuando un catálogo "deja de traer cosas", porque una marca por encima de los
 * contadores reales es justamente lo que hace que un registro no vuelva a
 * pedirse nunca. Antes la escribía `setLastSourceCode` desde la capa de la base
 * de datos, con el nombre interno de la entidad y en una línea aparte.
 */
export const anotarLectura = (entidad, { guardados, incremental = false, marca } = {}) => {
  const partes = [
    `${prefijo(entidad)} lectura ${incremental ? "incremental" : "completa"}`,
    `· ${separarMiles(guardados)} guardados`,
  ];

  if (marca !== undefined && marca !== null) partes.push(`· marca ${separarMiles(marca)}`);

  console.log(partes.join(" "));
};

/** La lectura terminó sin nada que guardar. No es un fallo. */
export const anotarSinNovedades = (entidad, incremental = false) => {
  console.log(
    `${prefijo(entidad)} ${
      incremental ? "sin novedades desde la última lectura" : "el servidor no devolvió registros"
    }`
  );
};

/** Un solo texto para lo que antes se decía de cuatro formas distintas. */
export const anotarSinConexion = (entidad) => {
  console.warn(`${prefijo(entidad)} sin conexión a internet, no se sincroniza`);
};

// ─── El transporte ─────────────────────────────────────────────────────────
//
// Familia aparte, y a propósito: los ayudantes de lectura son genéricos y NO
// saben a qué entidad sirven -- sólo conocen el endpoint de Business Central.
// Fingir que lo saben produciría prefijos inventados. Con `[bc:Kits]` queda
// claro que habla la capa de transporte, y sigue siendo filtrable.

const prefijoBC = (endpoint) => `[bc:${endpoint}]`;

export const anotarTransporte = (endpoint, texto) => {
  console.log(`${prefijoBC(endpoint)} ${texto}`);
};

export const anotarAvisoDeTransporte = (endpoint, texto) => {
  console.warn(`${prefijoBC(endpoint)} ${texto}`);
};

// ─── El proceso ────────────────────────────────────────────────────────────
//
// El orquestador no habla de una entidad: habla de la corrida entera. Con
// `[sync]` a secas queda claro que es el marco, y el mismo filtro `[sync` sigue
// mostrando todo -- el proceso y cada entidad debajo.

const PREFIJO_PROCESO = "[sync]";

export const anotarProceso = (texto) => {
  console.log(`${PREFIJO_PROCESO} ${texto}`);
};

export const anotarAvisoDeProceso = (texto) => {
  console.warn(`${PREFIJO_PROCESO} ${texto}`);
};

export const anotarErrorDeProceso = (contexto, error) => {
  console.error(`${PREFIJO_PROCESO} ${contexto}`, error);
};
